import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Overlay,
  Card,
  Header,
  Body,
  Footer,
  Grid,
  Col,
  TitleRow,
  RoleBadge,
  ScrollBox,
  Button,
  DangerButton,
  GhostButton,
  TextInput,
  MemberRow,
  MemberInfo,
  MemberActions,
  ActionButton,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  ConfirmationModal,
  ListBox,
  Row,
  Actions
} from './style';

import { apiUc } from '../../../../http/index';
import { toast } from 'react-toastify';

export default function GroupOptionsModal({
  isOpen,
  onClose,
  group,
  theme,
  currentUser,
  socket,
  onGroupUpdate
}) {
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [adms, setAdms] = useState(group?.members?.adms || group?.adms || []);
  const [usersRelatedByTeam, setUsersRelatedByTeam] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [addMembersQuery, setAddMembersQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const currentUserId = currentUser?.login || currentUser?.id;

  const socketRef = useRef(socket);
  const groupIdRef = useRef(group?.id);

  const getUserId = (u) => u.login ?? u.id ?? u.user_id;
  const getUserName = (u) => u.name ?? u.nome ?? u.full_name ?? u.login;

  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const login = localStorage.getItem('login');
        if (!login) return;

        const { data } = await apiUc.get(`/api/teams-users/${login}`);
        const users = JSON.parse(data?.auditorship?.detail || '[]');

        const currentMemberIds = members.map((m) => m.login || m.id);
        const filteredUsers = users.filter((u) => {
          const userId = getUserId(u);
          return userId !== login && !currentMemberIds.includes(userId);
        });

        setUsersRelatedByTeam(filteredUsers);
      } catch (err) {
        toast.error('Erro ao carregar usuários relacionados ao time', {
          autoClose: 3000,
          closeOnClick: true
        });
        console.error('Erro ao carregar usuários relacionados ao time:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isOpen, members]);

  const normalizeGroupMembers = (groupData) => {
    if (!groupData) return [];

    const normalizedMembers = [];

    if (groupData.members && typeof groupData.members === 'object') {
      if (Array.isArray(groupData.members.team)) {
        groupData.members.team.forEach((memberLogin) => {
          const isAdmin =
            groupData.members.adms &&
            groupData.members.adms.includes(memberLogin);

          normalizedMembers.push({
            id: memberLogin,
            login: memberLogin,
            name: memberLogin,
            role: isAdmin ? 'ADMIN' : 'MEMBER'
          });
        });
      }
    }

    if (normalizedMembers.length === 0) {
      if (Array.isArray(groupData.team)) {
        groupData.team.forEach((memberLogin) => {
          const isAdmin =
            groupData.adms && groupData.adms.includes(memberLogin);

          normalizedMembers.push({
            id: memberLogin,
            login: memberLogin,
            name: memberLogin,
            role: isAdmin ? 'ADMIN' : 'MEMBER'
          });
        });
      }

      if (Array.isArray(groupData.clients)) {
        groupData.clients.forEach((clientLogin) => {
          normalizedMembers.push({
            id: clientLogin,
            login: clientLogin,
            name: clientLogin,
            role: 'MEMBER'
          });
        });
      }
    }

    if (normalizedMembers.length === 0 && Array.isArray(groupData)) {
      groupData.forEach((member) => {
        if (typeof member === 'string') {
          normalizedMembers.push({
            id: member,
            login: member,
            name: member,
            role: 'MEMBER'
          });
        } else if (member && typeof member === 'object') {
          normalizedMembers.push({
            id: member.id || member.login || member.name,
            login: member.login || member.name,
            name: member.name || member.login,
            role: member.role || 'MEMBER'
          });
        }
      });
    }

    return normalizedMembers;
  };

  const computedPermissions = useMemo(() => {
    if (!group || !currentUser) return {};

    const normalizedMembers = normalizeGroupMembers(group);
    const currentMember = normalizedMembers.find(
      (m) =>
        (m.login || m.id) === currentUser?.login || m.id === currentUser?.login
    );

    if (!currentMember) return {};

    const role = currentMember.role;
    const isAdmin = role === 'ADMIN';

    return {
      canEditName: isAdmin,
      canAddMember: isAdmin,
      canRemoveMember: isAdmin,
      canPromote: isAdmin,
      canDemote: isAdmin,
      canDeleteGroup: isAdmin,
      canLeave: true,
      canViewHistory: true
    };
  }, [group, currentUser]);

  useEffect(() => {
    if (group) {
      setName(group.name || group.title || '');
      const normalizedMembers = normalizeGroupMembers(group);
      setMembers(normalizedMembers);
      setAdms(group?.members?.adms || group?.adms || []);
      groupIdRef.current = group.id;
    }
  }, [group]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    if (!socketRef.current) return;

    const handleMembersResponse = (data) => {
      if (data?.error) {
        setError('Usuário inválido');
        clearMessage('error');
        return;
      }

      const groupData = data.members || data;
      const normalized = normalizeGroupMembers(groupData);
      setMembers(normalized);

      if (groupData.adms) {
        setAdms(groupData.adms);
      }

      setSuccess('Membro atualizado com sucesso!');
      clearMessage('success');
    };

    const handleLeaveResponse = (data) => {
      if (data?.error) {
        setError('Erro ao sair do grupo');
        clearMessage('error');
        return;
      }

      if (data?.left && data.groupId === groupIdRef.current) {
        setSuccess('Você saiu do grupo com sucesso!');
        clearMessage('success');
        setShowConfirmLeave(false);
        if (typeof onClose === 'function') {
          onClose();
        }
        if (typeof onGroupUpdate === 'function') {
          onGroupUpdate({ ...group, removed: true });
        }
      }
    };

    const handleDeleteResponse = (data) => {
      if (data?.error) {
        setError('Erro ao excluir grupo');
        clearMessage('error');
        return;
      }

      if (data?.groupId === groupIdRef.current) {
        setSuccess('Grupo excluído com sucesso!');
        clearMessage('success');
        setShowConfirmDelete(false);
        if (typeof onClose === 'function') {
          onClose();
        }
        if (typeof onGroupUpdate === 'function') {
          onGroupUpdate({ ...group, removed: true });
        }
      }
    };

    socketRef.current.on('group:members', handleMembersResponse);
    socketRef.current.on('group:leave', handleLeaveResponse);
    socketRef.current.on('group:delete', handleDeleteResponse);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('group:members', handleMembersResponse);
        socketRef.current.off('group:leave', handleLeaveResponse);
        socketRef.current.off('group:delete', handleDeleteResponse);
      }
    };
  }, [onGroupUpdate, group]);

  useEffect(() => {
    if (!socketRef.current) return;

    const handleAdms = (data) => {
      if (!data || data.groupId !== groupIdRef.current) return;

      const nextAdms = Array.isArray(data.adms) ? data.adms : [];
      setAdms(nextAdms);

      setMembers((prev) =>
        (prev || []).map((m) => {
          const id = m.login || m.id;
          return { ...m, role: nextAdms.includes(id) ? 'ADMIN' : 'MEMBER' };
        })
      );

      setSuccess('Permissões atualizadas!');
      clearMessage('success');
    };

    socketRef.current.on('group:adms', handleAdms);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('group:adms', handleAdms);
      }
    };
  }, []);

  const filteredAvailableUsers = useMemo(() => {
    return (Array.isArray(usersRelatedByTeam) ? usersRelatedByTeam : []).filter(
      (u) =>
        getUserName(u)?.toLowerCase().includes(addMembersQuery.toLowerCase())
    );
  }, [usersRelatedByTeam, addMembersQuery]);

  const toggleUserSelection = (id) => {
    const s = new Set(selectedUsers);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedUsers(s);
  };

  const clearMessage = (type) => {
    setTimeout(() => {
      if (type === 'error') setError('');
      if (type === 'success') setSuccess('');
    }, 3000);
  };

  const handleUpdateGroupInfo = async () => {
    if (!socketRef.current || !groupIdRef.current) return;

    setLoading(true);
    setError('');

    try {
      socketRef.current.emit('group:info', {
        requestingUser: currentUserId,
        groupId: groupIdRef.current,
        title: name.trim()
      });

      setSuccess('Informações do grupo atualizadas com sucesso!');
      clearMessage('success');

      if (typeof onGroupUpdate === 'function') {
        onGroupUpdate({
          ...group,
          name: name.trim(),
          title: name.trim()
        });
      }
    } catch (err) {
      setError('Erro ao atualizar informações do grupo');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (
      !socketRef.current ||
      !groupIdRef.current ||
      !currentUserId ||
      selectedUsers.size === 0
    )
      return;

    setLoading(true);
    setError('');

    try {
      const selectedUserIds = Array.from(selectedUsers);
      const updatedMembers = [...members.map((m) => m.id), ...selectedUserIds];

      socketRef.current.emit('group:members', {
        groupId: groupIdRef.current,
        requestingUser: currentUserId,
        members: updatedMembers
      });

      setSelectedUsers(new Set());
      setSuccess('Solicitação de adição enviada!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao adicionar membro');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!socketRef.current || !groupIdRef.current || !currentUserId) return;

    setLoading(true);
    setError('');

    try {
      const updatedMembers = members
        .filter((m) => (m.login || m.id) !== memberId)
        .map((m) => m.id);

      socketRef.current.emit('group:members', {
        groupId: groupIdRef.current,
        requestingUser: currentUserId,
        members: updatedMembers
      });

      setSuccess('Solicitação de remoção enviada!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao remover membro');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteMember = async (memberId) => {
    if (!socketRef.current || !groupIdRef.current || !currentUserId) return;

    setLoading(true);
    setError('');

    try {
      const updatedAdms = [...new Set([...adms, memberId])];

      socketRef.current.emit('group:adms', {
        groupId: groupIdRef.current,
        requestingUser: currentUserId,
        adms: updatedAdms
      });

      setAdms(updatedAdms);
      setMembers((prev) =>
        prev.map((m) =>
          (m.login || m.id) === memberId ? { ...m, role: 'ADMIN' } : m
        )
      );

      setSuccess('Membro promovido a administrador!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao promover membro');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteMember = async (memberId) => {
    if (!socketRef.current || !groupIdRef.current || !currentUserId) return;

    setLoading(true);
    setError('');

    try {
      const updatedAdms = adms.filter((adm) => adm !== memberId);

      socketRef.current.emit('group:adms', {
        groupId: groupIdRef.current,
        requestingUser: currentUserId,
        adms: updatedAdms
      });

      setAdms(updatedAdms);
      setMembers((prev) =>
        prev.map((m) =>
          (m.login || m.id) === memberId ? { ...m, role: 'MEMBER' } : m
        )
      );

      setSuccess('Administrador destituído com sucesso!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao destituir administrador');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!socketRef.current || !groupIdRef.current) return;

    setLoading(true);
    setError('');

    try {
      socketRef.current.emit('group:delete', {
        requestingUser: currentUserId,
        groupId: groupIdRef.current
      });

      setSuccess('Solicitação de exclusão enviada!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao excluir grupo');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!socketRef.current || !groupIdRef.current) return;

    setLoading(true);
    setError('');

    try {
      socketRef.current.emit('group:leave', {
        requestingUser: currentUserId,
        groupId: groupIdRef.current
      });

      setSuccess('Solicitação de saída enviada!');
      clearMessage('success');
    } catch (err) {
      setError('Erro ao sair do grupo');
      clearMessage('error');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (memberId, newRole) => {
    if (newRole === 'ADMIN') {
      handlePromoteMember(memberId);
    } else if (newRole === 'MEMBER') {
      handleDemoteMember(memberId);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Header
            color={theme?.primary_color || '#2d2d2d'}
            fontColor={theme?.internal_chat_font_color || '#fff'}
          >
            <span>Configurações do Grupo</span>
            <GhostButton onClick={onClose} aria-label="Fechar">
              ×
            </GhostButton>
          </Header>

          <Body>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <Grid>
              <Col span={12}>
                <TitleRow>
                  <span>Informações do Grupo</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      disabled={!computedPermissions.canEditName || loading}
                      onClick={handleUpdateGroupInfo}
                    >
                      {loading ? <LoadingSpinner /> : 'Salvar'}
                    </Button>
                  </div>
                </TitleRow>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 4,
                      fontWeight: 500
                    }}
                  >
                    Nome do Grupo
                  </label>
                  <TextInput
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Digite o nome do grupo"
                    disabled={!computedPermissions.canEditName}
                  />
                </div>
              </Col>

              <Col span={12}>
                <TitleRow>
                  <span>Gerenciar Participantes</span>
                </TitleRow>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 8 }}>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Participantes Atuais
                    </small>
                  </div>
                  <ScrollBox>
                    {members.map((member) => {
                      const memberId = member.login || member.id;
                      const isCurrentUser =
                        (member.login || member.id) ===
                        (currentUser?.login || currentUser?.id);
                      const canManage =
                        computedPermissions.canPromote ||
                        computedPermissions.canDemote;
                      const canRemove =
                        computedPermissions.canRemoveMember && !isCurrentUser;

                      return (
                        <MemberRow key={memberId}>
                          <MemberInfo>
                            <div style={{ fontWeight: 600 }}>
                              {member.name || member.login}
                            </div>
                            {member.role === 'ADMIN' && (
                              <RoleBadge $role="ADMIN">Administrador</RoleBadge>
                            )}
                          </MemberInfo>

                          <MemberActions>
                            {canManage && (
                              <>
                                {member.role === 'ADMIN' && !isCurrentUser && (
                                  <ActionButton
                                    title="Destituir administrador"
                                    disabled={loading}
                                    onClick={() =>
                                      handleRoleChange(memberId, 'MEMBER')
                                    }
                                  >
                                    Destituir
                                  </ActionButton>
                                )}

                                {member.role !== 'ADMIN' && (
                                  <ActionButton
                                    title="Promover a administrador"
                                    disabled={loading}
                                    onClick={() =>
                                      handleRoleChange(memberId, 'ADMIN')
                                    }
                                  >
                                    Promover
                                  </ActionButton>
                                )}
                              </>
                            )}

                            {canRemove && (
                              <DangerButton
                                title={
                                  member.role === 'ADMIN'
                                    ? 'Não é possível remover administradores'
                                    : 'Remover do grupo'
                                }
                                disabled={loading || member.role === 'ADMIN'}
                                onClick={() =>
                                  member.role !== 'ADMIN' &&
                                  handleRemoveMember(memberId)
                                }
                              >
                                Remover
                              </DangerButton>
                            )}
                          </MemberActions>
                        </MemberRow>
                      );
                    })}

                    {members.length === 0 && (
                      <div
                        style={{
                          padding: 16,
                          textAlign: 'center',
                          color: '#888'
                        }}
                      >
                        Nenhum participante no grupo
                      </div>
                    )}
                  </ScrollBox>
                </div>

                {computedPermissions.canAddMember && (
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Adicionar Participantes
                      </small>
                    </div>
                    <TextInput
                      placeholder="Buscar pessoas..."
                      value={addMembersQuery}
                      onChange={(e) => setAddMembersQuery(e.target.value)}
                    />

                    {addMembersQuery.trim() && (
                      <ListBox>
                        {loadingUsers && (
                          <div style={{ padding: '12px', color: '#888' }}>
                            Carregando usuários...
                          </div>
                        )}
                        {!loadingUsers &&
                          filteredAvailableUsers.map((u) => {
                            const id = getUserId(u);
                            const checked = selectedUsers.has(id);
                            return (
                              <Row key={id}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleUserSelection(id)}
                                />
                                <span>{getUserName(u)}</span>
                              </Row>
                            );
                          })}
                        {!loadingUsers &&
                          filteredAvailableUsers.length === 0 && (
                            <div style={{ padding: '12px', color: '#888' }}>
                              Nenhum resultado
                            </div>
                          )}
                      </ListBox>
                    )}

                    <Actions>
                      <Button
                        disabled={
                          !computedPermissions.canAddMember ||
                          selectedUsers.size === 0 ||
                          loading
                        }
                        onClick={handleAddMember}
                      >
                        {loading ? (
                          <LoadingSpinner />
                        ) : (
                          `Adicionar ${selectedUsers.size} participante${
                            selectedUsers.size !== 1 ? 's' : ''
                          }`
                        )}
                      </Button>
                    </Actions>
                  </div>
                )}
              </Col>
            </Grid>
          </Body>

          <Footer>
            <div style={{ display: 'flex', gap: 8, marginRight: 'auto' }}>
              <Button onClick={onClose}>Fechar</Button>
              <Button
                disabled={!computedPermissions.canLeave || loading}
                onClick={() => setShowConfirmLeave(true)}
              >
                Sair do Grupo
              </Button>
            </div>

            {computedPermissions.canDeleteGroup && (
              <DangerButton
                disabled={loading}
                onClick={() => setShowConfirmDelete(true)}
              >
                Excluir Grupo
              </DangerButton>
            )}
          </Footer>
        </Card>
      </Overlay>

      {showConfirmDelete && (
        <ConfirmationModal>
          <div
            style={{
              background: 'white',
              padding: 24,
              borderRadius: 12,
              maxWidth: 400
            }}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Confirmar Exclusão</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666' }}>
              Tem certeza que deseja excluir este grupo? Esta ação não pode ser
              desfeita.
            </p>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <Button onClick={() => setShowConfirmDelete(false)}>
                Cancelar
              </Button>
              <DangerButton onClick={handleDeleteGroup} disabled={loading}>
                {loading ? <LoadingSpinner /> : 'Excluir'}
              </DangerButton>
            </div>
          </div>
        </ConfirmationModal>
      )}

      {showConfirmLeave && (
        <ConfirmationModal>
          <div
            style={{
              background: 'white',
              padding: 24,
              borderRadius: 12,
              maxWidth: 400
            }}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Confirmar Saída</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666' }}>
              Tem certeza que deseja sair deste grupo?
            </p>
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <Button onClick={() => setShowConfirmLeave(false)}>
                Cancelar
              </Button>
              <Button onClick={handleLeaveGroup} disabled={loading}>
                {loading ? <LoadingSpinner /> : 'Sair'}
              </Button>
            </div>
          </div>
        </ConfirmationModal>
      )}
    </>
  );
}
